import { useState, useMemo, useCallback, useEffect, useRef, Dispatch, SetStateAction } from "react"
import { useTranslation } from "react-i18next"
import { RiArrowDownSLine, RiArrowRightSLine, RiSearchLine } from "@remixicon/react"

import { useActivityOverviewListQuery, useActivityDetailQuery } from "@/hooks/api/useActivityQuery"
import { CircularProgress, MenuItem } from "@mui/material"

import { usePdfBuilderStore } from "@/stores/pdfBuilder"
import { usePdfDataContext } from "@/app/export/pdf/contexts/PdfDataContext"
import { useReportTemplateMetasQuery } from "@/hooks/api/useReportQuery"
import DatePicker from "@/components/form/datePicker/DatePicker"
import { endOfDay, format, parseISO, startOfDay } from "date-fns"
import { ACTIVITY_SESSION_TYPE, REPORT_TYPE } from "@/app/report/common/constants"
import { ActivityType } from "@/types/activity"
import type { ActivityParticipatedPlayerType } from "@/types/activity"
import { ReportStatus } from "@/types/report"
import { activityDateKey } from "@/app/export/pdf/utils/period"
import SessionSelector from "@/app/report/components/filter/sessionSelector/SessionSelector"
import PlayerSelector from "@/app/report/components/filter/playerSelector/PlayerSelector"
import Selector from "@/components/form/selector/Selector"

import "./reportFilters.scss"

export default function ReportFilters() {
  const { t } = useTranslation()
  const [isExpanded, setIsExpanded] = useState(true)
  const [searchKeyword, setSearchKeyword] = useState("")
  const [activityListOpen, setActivityListOpen] = useState(false)
  const [sessionOpen, setSessionOpen] = useState(false)
  const [playerOpen, setPlayerOpen] = useState(false)

  // Individual selectors to avoid re-rendering on unrelated store changes (e.g. drag state)
  const filterActivityId = usePdfBuilderStore((s) => s.filterActivityId)
  const filterSessionIds = usePdfBuilderStore((s) => s.filterSessionIds)
  const filterPlayerIds = usePdfBuilderStore((s) => s.filterPlayerIds)
  const filterReportMode = usePdfBuilderStore((s) => s.filterReportMode)
  const setFilterActivity = usePdfBuilderStore((s) => s.setFilterActivity)
  const setFilterSessions = usePdfBuilderStore((s) => s.setFilterSessions)
  const setFilterPlayers = usePdfBuilderStore((s) => s.setFilterPlayers)
  const setFilterReportMode = usePdfBuilderStore((s) => s.setFilterReportMode)
  const setFilterReportTemplate = usePdfBuilderStore((s) => s.setFilterReportTemplate)
  // 실제 적용 중인 리포트 템플릿(필터 미선택이면 최근 사용) — 컨텍스트가 정한다
  const { reportTemplateId, trendPlayerCandidates, trendActivityIds } = usePdfDataContext()
  const [templateOpen, setTemplateOpen] = useState(false)

  // 섹션 설정의 출처가 되는 리포트 템플릿 목록 — 리포트 화면 ReportFilter 와 같은 목록 (#75)
  // 리포트 종류·기간 — 트렌드는 액티비티·세션 대신 기간으로 범위를 정한다 (2026-10-02 요청)
  const reportKind = usePdfBuilderStore((s) => s.filterReportKind)
  const period = usePdfBuilderStore((s) => s.filterPeriod)
  const setPeriod = usePdfBuilderStore((s) => s.setPeriod)
  const isTrend = reportKind === "trend"

  const { data: reportTemplates } = useReportTemplateMetasQuery({
    reportType: isTrend ? REPORT_TYPE.TREND : REPORT_TYPE.DAILY,
  })

  const activityRef = useRef<HTMLDivElement>(null)

  // 고른 기간의 액티비티 — 기간을 바꾸면 쿼리 키가 바뀌어 다시 불러온다 (2026-10-02 요청).
  // /activities/recent-overview 는 기준일 이전 N개만 주어 옛 기간을 고르면 목록이 비었다
  const { data: activities, isLoading: isActivitiesLoading } = useActivityOverviewListQuery({
    start: startOfDay(parseISO(period.start)).valueOf(),
    end: endOfDay(parseISO(period.end)).valueOf(),
  })

  // Fetch detail for selected activity
  const { data: activityDetail } = useActivityDetailQuery(filterActivityId ?? 0, !!filterActivityId)

  // 분석이 끝난 액티비티만 고를 수 있게 한다 — 분석 전에는 리포트에 넣을 데이터가 없다.
  // 다른 화면(ActivitySelectorDialog, CSV 내보내기)과 같은 기준을 쓴다.
  // 기간 안의 분석 완료 액티비티. 목록 자체가 기간으로 조회되므로 여기서 기간을 다시 거르지 않는다.
  // /activities/overview 는 status 대신 analyzeFinished 로 알려 주기도 한다 (Quick Export 와 같은 기준)
  // 최신순 — 목록의 차례가 보장되지 않아 직접 정렬한다 (처음 들어올 때 최신 액티비티를 고르는 기준이기도 하다)
  const periodActivities = useMemo(
    () =>
      (activities ?? [])
        .filter((a) => a.status === ReportStatus.Analyzed || !!a.analyzeFinished)
        .sort((a, b) => activityDateKey(b).localeCompare(activityDateKey(a))),
    [activities],
  )
  const filteredActivities = useMemo(() => {
    const keyword = searchKeyword.trim().toLowerCase()

    return keyword
      ? periodActivities.filter((a) => a.name.toLowerCase().includes(keyword))
      : periodActivities
  }, [periodActivities, searchKeyword])

  // Players available based on selected sessions
  // 트렌드는 기간 안 액티비티들의 참가 선수가 후보다 — 액티비티·세션을 고르지 않는다 (2026-10-02 요청)
  const availablePlayers = useMemo(() => {
    if (isTrend) {
      return trendPlayerCandidates
    }
    const players = activityDetail?.players ?? []
    if (players.length === 0 || filterSessionIds.length === 0) {
      return []
    }

    // participatedSessions 는 세션별 매칭 데이터에서 만들어진다. 어느 선수에게도 없으면(세션 매칭 전 등)
    // 세션으로 걸러낼 근거가 없으므로 전체 참여 선수를 후보로 둔다 — 그렇지 않으면 선수 필터가 숨겨져
    // 선수를 고를 방법이 없고, "선수 0명 = 데이터 없음" 규칙에 걸려 모든 컴포넌트가 빈 채로 남는다.
    const hasParticipationInfo = players.some((p) => (p.participatedSessions?.length ?? 0) > 0)
    if (!hasParticipationInfo) {
      return players
    }

    return players.filter((p) =>
      p.participatedSessions?.some((sid) => filterSessionIds.includes(sid)),
    )
  }, [isTrend, trendPlayerCandidates, activityDetail, filterSessionIds])

  // 선택된 선수는 스토어(filterPlayerIds) 하나만을 출처로 삼는다.
  // PlayerSelector 는 선수 객체 배열 + useState 형태의 setter 를 요구하므로 여기서 변환만 한다.
  const selectedPlayerList = useMemo(
    () => availablePlayers.filter((p) => filterPlayerIds.includes(p.idfPlayer)),
    [availablePlayers, filterPlayerIds],
  )

  // setter 안에서 prev 를 읽어야 해서 최신 값을 ref 로 들고 있는다 (렌더 중 스토어 갱신을 피한다)
  const selectedPlayerListRef = useRef(selectedPlayerList)
  selectedPlayerListRef.current = selectedPlayerList

  const setSelectedPlayerList: Dispatch<SetStateAction<ActivityParticipatedPlayerType[]>> =
    useCallback(
      (action) => {
        const prev = selectedPlayerListRef.current
        const next = typeof action === "function" ? action(prev) : action
        setFilterPlayers(next.map((p) => p.idfPlayer))
      },
      [setFilterPlayers],
    )

  // 액티비티 리포트는 들어오자마자 가장 최근 액티비티로 시작한다 — 빈 화면을 보고 고르게 하지 않는다 (2026-10-02 요청).
  // 사용자가 고른 뒤에는 다시 끼어들지 않도록 한 번만 한다
  const autoPickedRef = useRef(false)
  // 기간을 바꿔 고른 액티비티가 그 기간 밖이면 선택을 비운다 — 아래에서 새 기간의 최신 액티비티로 다시 고른다
  useEffect(() => {
    if (isTrend || isActivitiesLoading || !filterActivityId) {
      return
    }
    if (periodActivities.some((a) => a.idfActivity === filterActivityId)) {
      return
    }
    autoPickedRef.current = false
    setFilterActivity(null)
  }, [isTrend, isActivitiesLoading, filterActivityId, periodActivities, setFilterActivity])

  useEffect(() => {
    if (isTrend || autoPickedRef.current || filterActivityId || periodActivities.length === 0) {
      return
    }
    const latest = periodActivities[0]
    if (!latest?.idfActivity) {
      return
    }
    autoPickedRef.current = true
    setFilterActivity(latest.idfActivity)
  }, [isTrend, filterActivityId, periodActivities, setFilterActivity])

  // 액티비티가 바뀌면 세션을 전부 선택하고, 모드는 리포트 화면의 기본 선택과 같게
  // 세션이 있으면 세션 모드·없으면 액티비티 모드로 되돌린다 (선수는 아래 availablePlayers 효과가 맡는다)
  useEffect(() => {
    setFilterSessions(activityDetail ? activityDetail.sessions.map((s) => s.idfSession) : [])
    if (activityDetail) {
      setFilterReportMode(
        activityDetail.sessions.length
          ? ACTIVITY_SESSION_TYPE.SESSION
          : ACTIVITY_SESSION_TYPE.ACTIVITY,
      )
    }
  }, [activityDetail, setFilterSessions, setFilterReportMode])

  // 선택 가능한 선수가 바뀌면(액티비티·세션 변경) 전원 선택으로 되돌린다.
  // 내용이 같은데 배열 ref 만 새로 생긴 경우는 건너뛴다 — 사용자의 해제가 되살아나는 것을 막는다.
  // 서명에 액티비티 ID 를 넣는 이유: 같은 팀의 다른 액티비티는 선수 집합이 동일해 선수 ID 만으로는
  // 구분되지 않는다. 그대로 두면 이전 액티비티에서 해제한 선수가 새 액티비티까지 따라온다.
  const availableSignatureRef = useRef<string | null>(null)
  useEffect(() => {
    const signature = `${isTrend ? "trend" : (filterActivityId ?? "")}:${availablePlayers.map((p) => p.idfPlayer).join(",")}`
    if (availableSignatureRef.current === signature) {
      return
    }
    availableSignatureRef.current = signature
    setFilterPlayers(availablePlayers.map((p) => p.idfPlayer))
  }, [isTrend, filterActivityId, availablePlayers, setFilterPlayers])

  // Close activity dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (activityRef.current && !activityRef.current.contains(e.target as Node)) {
        setActivityListOpen(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)

    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  const selectedActivityName = useMemo(() => {
    if (!filterActivityId || !activities) {
      return ""
    }

    return activities.find((a) => a.idfActivity === filterActivityId)?.name ?? ""
  }, [filterActivityId, activities])

  const handleSelectActivity = useCallback(
    (id: number) => {
      setFilterActivity(filterActivityId === id ? null : id)
      setActivityListOpen(false)
      setSearchKeyword("")
    },
    [filterActivityId, setFilterActivity],
  )

  const getActivityTypeLabel = (type: string) => {
    const key = ActivityType[type as keyof typeof ActivityType]

    return key ? t(key) : type
  }

  return (
    <div className="ReportFiltersCont">
      <div className={`report-filters-box ${isExpanded ? "report-filters-box--expanded" : ""}`}>
        {/* Header */}
        <div className="report-filters-header" onClick={() => setIsExpanded(!isExpanded)}>
          {isExpanded ? <RiArrowDownSLine size={18} /> : <RiArrowRightSLine size={18} />}
          <span className="report-filters-title">{t("reportFilters", "Report Filters")}</span>
        </div>

        {isExpanded && (
          <div className="report-filters-body">
            {/* 기간 — 액티비티 목록(액티비티 리포트)·집계 범위(트렌드)를 정한다 (2026-10-02 요청) */}
            <div className="report-filters-field">
              <label className="report-filters-label">{t("period", "Period")}</label>
              <div className="report-filters-period">
                <DatePicker
                  value={period.start ? parseISO(period.start) : null}
                  defaultValueNull
                  onChange={(date: Date | null) =>
                    setPeriod({
                      ...period,
                      start: date ? format(date, "yyyy-MM-dd") : period.start,
                    })
                  }
                />
                <span>~</span>
                <DatePicker
                  value={period.end ? parseISO(period.end) : null}
                  defaultValueNull
                  onChange={(date: Date | null) =>
                    setPeriod({ ...period, end: date ? format(date, "yyyy-MM-dd") : period.end })
                  }
                />
              </div>
            </div>

            {/* Activity Select — 액티비티 리포트만 (트렌드는 기간 안의 액티비티 전체) */}
            {!isTrend && (
              <div className="report-filters-field" ref={activityRef}>
                <label className="report-filters-label">{t("activity")}</label>
                <button
                  className="report-filters-select-btn"
                  onClick={() => setActivityListOpen((prev) => !prev)}>
                  <span className="select-btn-text">
                    {selectedActivityName || t("selectActivity", "Select activity...")}
                  </span>
                  <RiArrowDownSLine
                    size={16}
                    className={`select-btn-arrow ${activityListOpen ? "select-btn-arrow--open" : ""}`}
                  />
                </button>
                {activityListOpen && (
                  <div className="report-filters-dropdown report-filters-dropdown--activity">
                    <div className="report-filters-search">
                      <RiSearchLine size={14} className="search-icon" />
                      <input
                        className="search-input"
                        type="text"
                        value={searchKeyword}
                        onChange={(e) => setSearchKeyword(e.target.value)}
                        placeholder={t("searchActivity", "Search activity...")}
                        autoFocus
                      />
                    </div>
                    <div className="report-filters-activity-list">
                      {/* 목록을 받는 동안은 "결과 없음" 대신 스피너 (2026-10-01 보고) */}
                      {isActivitiesLoading ? (
                        <div className="activity-list-empty">
                          <CircularProgress size={18} color="inherit" />
                        </div>
                      ) : (
                        filteredActivities.length === 0 && (
                          <div className="activity-list-empty">{t("noResults", "No results")}</div>
                        )
                      )}
                      {filteredActivities.map((activity) => (
                        <div
                          key={activity.idfActivity}
                          className={`activity-list-item ${filterActivityId === activity.idfActivity ? "activity-list-item--selected" : ""}`}
                          onClick={() => handleSelectActivity(activity.idfActivity!)}>
                          <div className="activity-list-item-info">
                            <span className="activity-name">{activity.name}</span>
                            <span className="activity-meta">
                              {getActivityTypeLabel(activity.type)}
                              {activity.playerCount != null && ` · ${activity.playerCount}P`}
                            </span>
                          </div>
                          <span className="activity-date">{activityDateKey(activity)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Activity / Session mode + Session Filter — 리포트 화면과 같은 선택기 (#75) */}
            {!isTrend && filterActivityId && activityDetail && (
              <div className="report-filters-field">
                <label className="report-filters-label">
                  {t("activity")} / {t("session")}
                </label>
                <SessionSelector
                  open={sessionOpen}
                  setOpen={setSessionOpen}
                  selectedType={filterReportMode}
                  setSelectedType={setFilterReportMode}
                  selectedSessionList={filterSessionIds}
                  setSelectedSessionList={setFilterSessions}
                  detail={activityDetail}
                />
              </div>
            )}

            {/* Player Filter */}
            {(isTrend || !!filterActivityId) && availablePlayers.length > 0 && (
              <div className="report-filters-field">
                <label className="report-filters-label">{t("player")}</label>
                <PlayerSelector
                  open={playerOpen}
                  setOpen={setPlayerOpen}
                  selectedActivity={
                    isTrend ? trendActivityIds : filterActivityId ? [filterActivityId] : []
                  }
                  players={availablePlayers}
                  selectedPlayerList={selectedPlayerList}
                  setSelectedPlayerList={setSelectedPlayerList}
                  paramsVersion={false}
                />
              </div>
            )}

            {/* Report Template — Summary 등 섹션 설정의 출처. 리포트 화면의 템플릿 선택과 같은 목록 (#75) */}
            {!!reportTemplates?.length && (
              <div className="report-filters-field">
                <label className="report-filters-label">{t("template", "Template")}</label>
                <Selector
                  MenuProps={{ PaperProps: { className: "PropertyPanel__menu" } }}
                  type="select"
                  open={templateOpen}
                  onOpen={() => setTemplateOpen(true)}
                  onClose={() => setTemplateOpen(false)}
                  className="report-filters-template-selector"
                  value={reportTemplateId ?? ""}
                  onChange={(e: any) => {
                    setTemplateOpen(false)
                    setFilterReportTemplate((e.target.value as number) || null)
                  }}
                  renderValue={(value) => (
                    <span className="selected-text">
                      {reportTemplates.find((template) => template.idfTemplate === value)?.name ??
                        ""}
                    </span>
                  )}>
                  {reportTemplates.map((template) => (
                    <MenuItem key={template.idfTemplate} value={template.idfTemplate}>
                      {template.name}
                    </MenuItem>
                  ))}
                </Selector>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
